'use client';

import { createContext, createElement, useContext, type ReactNode } from 'react';
import type { NavStanding, WorkRail } from '@goodbuild/rules/site';

/**
 * Who is signed in, as far as the chrome is concerned.
 *
 * The navigation needs two things — whether to show the account links, and
 * what to render where the account widget goes — and it cannot work either out
 * for itself. Three applications answer them three ways: `auth()` in a Next
 * server component, `useAuth()` in the React app, `Astro.locals.auth()` in the
 * storefront, each with a different session source behind it.
 *
 * A context rather than props, because `Page` renders the nav for you and
 * threading two arguments through every page that exists would be worse than
 * this. The default is signed out, which is the safe way to be wrong: it shows
 * a sign-in link to somebody who is already signed in, rather than showing
 * account navigation to somebody who is not.
 */
export interface AccountState {
  signedIn: boolean;
  /** The application's account menu, whatever it renders one from. */
  account?: ReactNode;
  /**
   * Which role links the top bar may draw.
   *
   * Third thing the chrome cannot work out for itself: the React app has `/me`
   * and knows exactly, and the storefront reads a hint the app leaves it in a
   * cookie — four booleans, no name and no authority, which is as much as a
   * different origin may be told. Undefined means *nobody told us*, which the
   * bar reads as no roles — a link too few, which costs a detour through the
   * account page, rather than a link too many, which is a door that opens onto
   * a refusal.
   *
   * Undefined is a real state on both. The React app is in it for the moment
   * between a session being confirmed and `/me` answering, and any surface that
   * renders this chrome without an `AccountProvider` is in it permanently.
   */
  standing?: NavStanding | undefined;
  /**
   * What the rail down the side holds, when the audience's list is not it.
   *
   * Fourth thing the chrome cannot work out for itself, and the one that is
   * least obviously a chrome question. `WORK_NAV` is a constant, so the
   * developer portal drew `Statistics` and `Builds` for a studio nobody had
   * chosen (#88) and the creators' rail offered the board to somebody who had
   * not applied for it (#90) — both of them navigations describing a state the
   * reader is not in.
   *
   * A function of the ground being painted rather than a list, because one
   * route renders either audience: `/onboarding` shows a studio registration or
   * a creator application, and `Page` is the only thing that knows which by the
   * time the rail is drawn. See `WorkRail`.
   *
   * Unset is the ordinary state and means `WORK_NAV`, which is what the
   * storefront, a test and any surface without a provider get.
   */
  rail?: WorkRail | undefined;
}

const AccountContext = createContext<AccountState>({ signedIn: false });

export function AccountProvider({
  signedIn,
  account,
  standing,
  rail,
  children,
}: AccountState & { children: ReactNode }): ReactNode {
  return createElement(
    AccountContext.Provider,
    { value: { signedIn, account, standing, rail } },
    children,
  );
}

export function useAccount(): AccountState {
  return useContext(AccountContext);
}
